/**
 * terra-qyt.12 (U8) — the review-stage body's per-step decision list.
 *
 * `GenerateWithAIDrawer`'s review stage renders only a tally/alerts summary
 * BY DESIGN (that file's own doc, `:73-81`): the per-step Current/Generated
 * cards live INLINE on the page beside the push drawer, not inside it. At "N
 * of N reviewed" the drawer body has nothing left to say — this module is
 * the fix: a compact projection of each step's decision, so the drawer can
 * render one jump-to-card row per step without owning the cards themselves.
 *
 * DOM-free on purpose, the same reason every other pure module under
 * `mini-apps/playbooks/` is (`rung-counts.ts`'s own doc): `bun:test` has no
 * DOM under `mini-apps/`, so anything a Glaze-importing `.tsx` file needs
 * tested lives here instead.
 */

import type { GeneratedCopyEntry, StepReview } from './generated-copy-store';
import { sortStepsByPosition, stepOrdinal } from './gong-flow-picker-api';
import type { SequenceStep } from './schemas';

export type ReviewStepLabel = 'Accepted' | 'Rejected' | 'Edited' | 'Pending' | 'Unchanged';

export interface ReviewStepRow {
  position: number;
  /**
   * QA round 7, U5 (item 7) — THE step number the drawer shows ("Step
   * {ordinal} · {label}"), the same number the editor header and
   * `DispatchInspector` use. `headSteps`' stored order is not guaranteed
   * contiguous from 0 (`SequenceStepsSchema` pins uniqueness only), so this is
   * NOT always `position + 1`.
   */
  ordinal: number;
  label: ReviewStepLabel;
}

function labelFor(review: StepReview): ReviewStepLabel {
  if (review.decision === 'pending') return 'Pending';
  if (review.decision === 'rejected') return 'Rejected';
  if (review.edited !== null) return 'Edited';
  return review.unchanged ? 'Unchanged' : 'Accepted';
}

/**
 * Iterates `headSteps` POSITION-SORTED (not `entry.steps`, and not
 * `headSteps`' own possibly-unordered array order — QA round 7, U5), the same
 * direction `resolveApprovedSteps` iterates (`generated-copy-store.ts:595-604`):
 * a staged position with no matching head step produces no row, and the
 * output order follows ascending POSITION rather than `Object.values(entry.steps)`'s
 * insertion order or `headSteps`' stored array order.
 */
export function reviewStepRows(entry: GeneratedCopyEntry, headSteps: readonly SequenceStep[]): ReviewStepRow[] {
  if (entry.status === 'error') return [];
  const sorted = sortStepsByPosition(headSteps);
  const rows: ReviewStepRow[] = [];
  for (const head of sorted) {
    const review = entry.steps[head.position];
    if (review === undefined) continue;
    rows.push({ position: head.position, ordinal: stepOrdinal(sorted, head.position), label: labelFor(review) });
  }
  return rows;
}
